import React, { useRef, useState, useEffect, useMemo, useCallback, useContext } from "react";
import { motion } from "framer-motion";
import { FaHeart, FaShare, FaPlay, FaPause } from "react-icons/fa";
import { useLocation, useParams, Link } from "react-router-dom";
import { Star, Trophy, Users, Home, RotateCcw, Gamepad2, Joystick, Info, Keyboard, Tag } from "lucide-react";
import { GoScreenFull } from "react-icons/go";
import { useSEO } from "../Pages/Seo";
import AOS from "aos";
import "aos/dist/aos.css";
import { DataContext } from "../Context/DataContext";
import { Helmet } from "react-helmet";

const PlayGame = () => {
  const { slug } = useParams();
  const location = useLocation();
  const iframeRef = useRef(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isFavorite, setIsFavorite] = useState(false);
  const [isGamePaused, setIsGamePaused] = useState(false);
  const gameRating = useMemo(() => 4.5, []);
  const playCount = useMemo(() => 1247, []);
  const [gameStarted, setGameStarted] = useState(false);
  const AllGames = useContext(DataContext) || [];

  useEffect(() => {
    AOS.init({
      duration: 500,
      easing: "ease-in-out",
      once: true,
    });
  }, []);

  const createSlug = (text) => {
    return text
      ?.toLowerCase()
      .trim()
      .replace(/[^\w\s-]/g, '')
      .replace(/[\s_-]+/g, '-')
      .replace(/^-+|-+$/g, '');
  };

  const game = useMemo(() => {
    if (location.state?.game) return location.state.game;

    if (AllGames.length > 0 && slug) {
      return AllGames.find(g => createSlug(g.name) === slug);
    }
    return null;
  }, [location.state, AllGames, slug]);

  const getGameUrl = useCallback((originalUrl) => {
    if (!originalUrl) return "";

    let decodedUrl = originalUrl.includes("http")
      ? originalUrl
      : atob(originalUrl);

    if (decodedUrl.includes("gamedistribution.com")) {
      const baseUrl = decodedUrl.split("?")[0];
      const currentUrl = window.location.origin;
      return `${baseUrl}?gd_sdk_referrer_url=${encodeURIComponent(currentUrl)}`;
    }

    return decodedUrl;
  }, []);

  const gameUrl = useMemo(() => {
    return getGameUrl(game?.iframeSrc || game?.link);
  }, [game, getGameUrl]);


  const handleFullScreen = useCallback(() => {
    const elem = iframeRef.current;
    if (!elem) return;

    const isCurrentlyFullscreen =
      document.fullscreenElement ||
      document.webkitFullscreenElement ||
      document.mozFullScreenElement ||
      document.msFullscreenElement;

    if (!isCurrentlyFullscreen) {
      if (elem.requestFullscreen) {
        elem.requestFullscreen().catch((err) => console.log("Fullscreen error:", err));
      } else if (elem.webkitRequestFullscreen) {
        elem.webkitRequestFullscreen(); // Safari
      } else if (elem.mozRequestFullScreen) {
        elem.mozRequestFullScreen(); // Firefox
      } else if (elem.msRequestFullscreen) {
        elem.msRequestFullscreen(); // IE/Edge
      }
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      } else if (document.webkitExitFullscreen) {
        document.webkitExitFullscreen(); // Safari
      } else if (document.mozCancelFullScreen) {
        document.mozCancelFullScreen(); // Firefox
      } else if (document.msExitFullscreen) {
        document.msExitFullscreen(); // IE/Edge
      }
    }
  }, []);

  useEffect(() => {
    const handleChange = () => {
      const isCurrentlyFullscreen = !!(
        document.fullscreenElement ||
        document.webkitFullscreenElement ||
        document.mozFullScreenElement ||
        document.msFullscreenElement
      );
      setIsFullscreen(isCurrentlyFullscreen);
    };

    document.addEventListener("fullscreenchange", handleChange);
    document.addEventListener("webkitfullscreenchange", handleChange); // Safari
    document.addEventListener("mozfullscreenchange", handleChange);    // Firefox
    document.addEventListener("MSFullscreenChange", handleChange);     // IE/Edge

    return () => {
      document.removeEventListener("fullscreenchange", handleChange);
      document.removeEventListener("webkitfullscreenchange", handleChange);
      document.removeEventListener("mozfullscreenchange", handleChange);
      document.removeEventListener("MSFullscreenChange", handleChange);
    };
  }, []);

  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, []);

  const handleIframeLoad = useCallback(() => {
    setTimeout(() => setIsLoading(false), 1000);
  }, []);


  const handleFavorite = useCallback(() => {
    setIsFavorite(prev => !prev);
  }, []);
  

  const handleShare = useCallback(async () => {
    if (!game) return;
    if (navigator.share) {
      await navigator.share({
        title: game.name,
        text: `Play ${game.name} on Game Zone for free!`,
        url: window.location.href,
      });
    } else {
      navigator.clipboard.writeText(window.location.href);
      alert("Game link copied!");
    }
  }, [game]);


  const seoData = useMemo(() => ({
    title: game ? `Play ${game.name} Free Online | Game Zone Hub` : "Game Not Found",
    description: game ? `Play ${game.name} free online directly in your browser. ${game.desc || "Enjoy the best free HTML5 games with no downloads at Game Zone Hub."}` : "Game not found.",
    keywords: game ? `play ${game.name}, ${game.name} free online, play ${game.name} game, browser games, free online games, Game Zone Hub` : "free games",
    canonical: `https://www.gamezonehub.in/play/${slug}`,
  }), [game, slug]);  

  useSEO(seoData);

  const relatedGames = useMemo(() => {
      if (!game || AllGames.length === 0) return [];
      return AllGames.filter(g => g.name !== game.name).slice(0, 4);
  }, [game, AllGames]);


  if (!game) {
    return (
      <motion.div
        className="min-h-screen bg-gradient-to-br from-[#141414] via-[#1f1f1f] to-[#141414] flex items-center justify-center p-4"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      >
        <motion.div
          className="text-center backdrop-blur-lg rounded-3xl p-8 sm:p-12 border border-white/20 max-w-md w-full"
          initial={{ scale: 0.9, opacity: 0, y: 30 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
        >
          <motion.div
            className="text-4xl sm:text-6xl mb-6"
            animate={{ y: [0, -10, 0], rotate: [0, 5, -5, 0] }}
            transition={{ duration: 3, repeat: Infinity }}
          >
            🎮
          </motion.div>
          <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4">Game Not Found</h2>
          <p className="text-gray-300 mb-6 text-sm sm:text-base">The game you're looking for doesn't exist or has been removed.</p>
          <motion.div
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-500 to-cyan-500 text-white font-bold rounded-full hover:from-blue-600 hover:to-cyan-600 transition-all duration-300"
            >
              <Home size={18} /> Back to Games
            </Link>
          </motion.div>
        </motion.div>
      </motion.div>
    );
  }

  const schemaData = game ? {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "VideoGame",
        "name": game.name,
        "description": game.desc || `Play ${game.name} online for free at Game Zone Hub.`,
        "url": `https://www.gamezonehub.in/play/${slug}`,
        "image": game.image,
        "genre": "Browser Game",
        "playMode": "SinglePlayer",
        "applicationCategory": "Game",
        "operatingSystem": "Web Browser",
        "offers": {
          "@type": "Offer",
          "price": "0",
          "priceCurrency": "USD",
          "availability": "https://schema.org/InStock"
        },
        "aggregateRating": {
          "@type": "AggregateRating",
          "ratingValue": "4.5",
          "bestRating": "5",
          "ratingCount": "1247"
        }
      },
      {
        "@type": "BreadcrumbList",
        "itemListElement": [
          {
            "@type": "ListItem",
            "position": 1,
            "name": "Home",
            "item": "https://www.gamezonehub.in/"
          },
          {
            "@type": "ListItem",
            "position": 2,
            "name": "Games",
            "item": "https://www.gamezonehub.in/games"
          },
          {
            "@type": "ListItem",
            "position": 3,
            "name": game.name
          }
        ]
      }
    ]
  } : null;

  return (
    <motion.div
      className="min-h-screen pt-11 bg-gradient-to-br from-[#141414] via-[#1f1f1f] to-[#141414] relative overflow-hidden"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      <Helmet>
        {schemaData && (
          <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData) }} />
        )}
      </Helmet>

      <div className="container mx-auto px-4">
        {/* Animated background */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <motion.div
            className="absolute -top-4 -left-4 w-48 sm:w-72 h-48 sm:h-72 bg-blue-500/20 rounded-full blur-3xl"
            animate={{ scale: [1, 1.2, 1], opacity: [0.2, 0.4, 0.2] }}
            transition={{ duration: 6, repeat: Infinity }}
          />
          <motion.div
            className="absolute top-1/2 -right-4 w-64 sm:w-96 h-64 sm:h-96 bg-cyan-500/20 rounded-full blur-3xl"
            animate={{ scale: [1.2, 1, 1.2], opacity: [0.2, 0.4, 0.2] }}
            transition={{ duration: 8, repeat: Infinity }}
          />
          <motion.div
            className="absolute -bottom-4 left-1/2 w-56 sm:w-80 h-56 sm:h-80 bg-purple-500/20 rounded-full blur-3xl"
            animate={{ scale: [1, 1.3, 1], opacity: [0.2, 0.4, 0.2] }}
            transition={{ duration: 7, repeat: Infinity }}
          />
        </div>

        <div className="relative z-10 pt-16 sm:pt-20 pb-6 sm:pb-8 px-3 sm:px-4">
            
          <nav className="flex text-gray-400 text-sm mb-6 ml-2" aria-label="Breadcrumb">
              <ol className="inline-flex items-center space-x-1 md:space-x-3">
                  <li className="inline-flex items-center">
                      <Link to="/" className="hover:text-white transition-colors">Home</Link>
                  </li>
                  <li>/</li>
                  <li>
                      <Link to="/games" className="hover:text-white transition-colors">Games</Link>
                  </li>
                  <li>/</li>
                  <li className="text-blue-400 font-medium truncate max-w-[150px]">{game.name}</li>
              </ol>
          </nav>

          <div className="max-w-7xl mx-auto mb-6 sm:mb-8" data-aos="fade-down">
            <div className="text-center mb-4 sm:mb-6">
              <h1
                className="text-3xl sm:text-4xl md:text-5xl xl:text-6xl font-extrabold 
              text-transparent bg-clip-text bg-gradient-to-r from-blue-400 via-cyan-400 to-white 
              mb-3 sm:mb-4 animate-pulse leading-tight flex items-center justify-center gap-3"
                data-aos="zoom-in"
              >
                <Gamepad2 className="w-8 h-8 sm:w-10 sm:h-10 text-blue-400 drop-shadow-[0_0_10px_rgba(59,130,246,0.7)]" />
                  Play {game.name}
                <Joystick className="w-8 h-8 sm:w-10 sm:h-10 text-cyan-400 drop-shadow-[0_0_10px_rgba(34,211,238,0.7)]" />
              </h1>

              <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-6 text-white/80 text-xs sm:text-sm md:text-base" data-aos="fade-up">
                <div className="flex items-center gap-2">
                  <Star className="text-yellow-400" size={16} />
                  <span>{gameRating}/5</span>
                </div>
                <div className="flex items-center gap-2">
                  <Users className="text-cyan-400" size={16} />
                  <span>{playCount.toLocaleString()} plays</span>
                </div>
              </div>
            </div>

            <div className="bg-black/30 sm:mt-0 mt-7 sm:mb-0 mb-7 backdrop-blur-lg rounded-xl sm:rounded-2xl p-3 sm:p-4 mb-4 sm:mb-6 border border-white/10" data-aos="fade-up">
              <div className="flex flex-col sm:flex-row items-center justify-between gap-3 sm:gap-4">
                <div className="flex items-center gap-2 sm:gap-3 w-full sm:w-auto" data-aos="zoom-in-up">
                  <button
                    onClick={() => setIsGamePaused(!isGamePaused)}
                    className="flex items-center gap-2 px-3 sm:px-4 py-2 bg-white/10 hover:bg-white/20 rounded-lg text-white transition-all duration-300 text-sm sm:text-base flex-1 sm:flex-none justify-center"
                    aria-label={isGamePaused ? "Resume game" : "Pause game"}
                  >
                    {isGamePaused ? <FaPlay size={14} /> : <FaPause size={14} />}
                    {isGamePaused ? 'Resume' : 'Pause'}
                  </button>

                </div>

                <div className="flex items-center gap-3 sm:gap-3  w-full sm:w-auto justify-center" data-aos="zoom-in">
                  <button
                    onClick={handleFavorite}
                    aria-label="Add game to favorites"
                    className={`p-2 sm:p-3 rounded-full transition-all duration-300 transform hover:scale-110 ${isFavorite
                        ? 'bg-blue-500 text-white shadow-lg shadow-blue-500/30'
                        : 'bg-white/10 text-white hover:bg-white/20'
                      }`}
                    title="Add to Favorites"
                  >
                    <FaHeart size={16} />
                  </button>

                  <button
                    onClick={handleShare}
                    aria-label="Share this game"
                    className="p-2 sm:p-3 rounded-full bg-white/10 hover:bg-white/20 text-white transition-all duration-300 transform hover:scale-110"
                    title="Share Game"
                  >
                    <FaShare size={16} />
                  </button>

                  <button
                    onClick={handleFullScreen}
                    aria-label={isFullscreen ? "Exit fullscreen" : "Enter fullscreen"}
                    className={`relative flex items-center gap-1 sm:gap-2 px-3 sm:px-4 py-2 rounded-full font-semibold transition-all duration-500 transform text-xs sm:text-sm overflow-hidden group ${isFullscreen
                        ? 'bg-gradient-to-r from-blue-500 to-cyan-500 text-white shadow-xl shadow-blue-500/30 scale-105 animate-pulse'
                        : 'bg-gradient-to-r from-cyan-500 to-blue-600 text-white hover:from-cyan-600 hover:to-blue-700 hover:scale-105'
                      }`}
                    data-aos="zoom-in"
                  >
                    <div className={`absolute inset-0 bg-gradient-to-r transition-all duration-1000 ${isFullscreen
                        ? 'from-blue-400 to-cyan-400 opacity-20 animate-ping'
                        : 'from-blue-400 to-cyan-400 opacity-0 group-hover:opacity-20'
                      }`}></div>

                    <div className={`absolute inset-0 -skew-x-12 bg-gradient-to-r from-transparent via-white/20 to-transparent transition-transform duration-1000 ${isFullscreen ? 'animate-pulse' : 'transform translate-x-[-100%] group-hover:translate-x-[200%]'
                      }`}></div>

                    <div className="relative z-10 flex items-center gap-1 sm:gap-2">
                      <div className={`transition-transform duration-300 animate-pulse-scale ' ${isFullscreen ? 'animate-spin' : 'group-hover:rotate-12'}`}>
                        {isFullscreen ? <GoScreenFull size={16} /> : <GoScreenFull size={16} />}
                      </div>
                      <span className="inline">
                        {isFullscreen ? 'Exit FullScreen' : 'Full Screen'}
                      </span>
                    </div>
                    {isFullscreen && (
                      <div className="absolute -inset-1 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-full opacity-30 animate-ping"></div>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="max-w-7xl mx-auto px-2 sm:px-0" data-aos="fade-up">
            <div className="relative bg-black/20 backdrop-blur-sm rounded-2xl sm:rounded-3xl p-1 border border-white/20 shadow-2xl" data-aos="zoom-in-up">
              {isLoading && (
                <div className="absolute inset-0 z-20 flex items-center justify-center bg-black/50 backdrop-blur-sm rounded-2xl sm:rounded-3xl" data-aos="zoom-in">
                  <div className="text-center text-white p-4">
                    <div className="w-12 sm:w-16 h-12 sm:h-16 border-4 border-white/20 border-t-white rounded-full animate-spin mx-auto mb-4"></div>
                    <p className="text-lg sm:text-xl font-semibold mb-2">Loading Game...</p>
                    <p className="text-white/70 text-sm sm:text-base">Get ready for an amazing experience!</p>
                  </div>
                </div>
              )}

              <div className="relative rounded-xl sm:rounded-[20px] overflow-hidden">
                <iframe
                  id="game"
                  ref={iframeRef}
                  src={gameUrl}
                  title={`Play ${game.name} online`}
                  className="w-full h-[400px] xs:h-[300px] sm:h-[400px] md:h-[500px] lg:h-[600px] xl:h-[700px]                 border-0 rounded-xl sm:rounded-[20px]"
                  allow="fullscreen" // Add this
                  allowFullScreen
                  scrolling="yes"
                  onLoad={handleIframeLoad}
                  style={{
                    opacity: isLoading ? 0 : 1,
                    transition: "opacity 0.5s ease-in-out",
                  }}
                  sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
                />

                {!gameStarted && (
                  <div className="absolute inset-0 z-30 flex items-center justify-center bg-black/70 backdrop-blur-sm rounded-xl sm:rounded-[20px]" data-aos="zoom-in">
                    <button
                      onClick={() => {
                        handleFullScreen();
                        setGameStarted(true);
                      }}
                      className="px-6 py-3 bg-gradient-to-r from-blue-500 to-cyan-500 text-white font-bold rounded-full hover:from-blue-600 hover:to-cyan-600 transition-all duration-300 transform hover:scale-105 text-lg sm:text-xl flex items-center gap-2"
                    >
                      <GoScreenFull size={20} /> Full Screen & Play
                    </button>
                  </div>
                )}
              </div>


              {isGamePaused && (
                <div className="absolute inset-0 z-10 flex items-center justify-center bg-black/50 backdrop-blur-sm rounded-2xl sm:rounded-3xl" data-aos="zoom-in">
                  <div className="text-center text-white p-4">
                    <FaPause className="text-4xl sm:text-6xl mb-4 mx-auto opacity-50" />
                    <p className="text-xl sm:text-2xl font-bold mb-4">Game Paused</p>
                    <button
                      onClick={() => setIsGamePaused(false)}
                      className="px-4 sm:px-6 py-2 sm:py-3 bg-gradient-to-r from-blue-500 to-cyan-500 text-white font-bold rounded-full hover:from-blue-600 hover:to-cyan-600 transition-all duration-300 transform hover:scale-105 text-sm sm:text-base"
                    >
                      <FaPlay className="inline mr-2" /> Resume Game
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="max-w-7xl mx-auto mt-6 sm:mt-8" data-aos="fade-up">
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4 sm:px-0 px-5">
              <Link
                to="/games"
                className="flex items-center gap-2 sm:gap-3 px-6 sm:px-8 py-3 sm:py-4 bg-gradient-to-r from-blue-600 to-cyan-600 text-white font-bold rounded-full shadow-xl hover:from-blue-700 hover:to-cyan-700 transition-all duration-300 transform hover:scale-105 hover:shadow-2xl text-sm sm:text-base w-full sm:w-auto justify-center"
                data-aos="fade-right"
              >
                <Gamepad2 size={20} />
                Back to Games
              </Link>

              <button
                onClick={() => window.location.reload()}
                className="flex items-center gap-2 sm:gap-3 px-6 sm:px-8 py-3 sm:py-4 bg-gradient-to-r from-cyan-500 to-blue-500 text-white font-bold rounded-full shadow-xl hover:from-cyan-600 hover:to-blue-600 transition-all duration-300 transform hover:scale-105 hover:shadow-2xl text-sm sm:text-base w-full sm:w-auto justify-center"
                data-aos="fade-left"
              >
                <RotateCcw size={20} />
                New Game
              </button>
            </div>
          </div>
          
          <div className="grid lg:grid-cols-3 gap-8 mt-12 max-w-7xl mx-auto">
              
              <div className="lg:col-span-2 space-y-8">
                  
                  {/* COMPREHENSIVE SEO-OPTIMIZED GAME ARTICLE */}
                  <article className="bg-white/5 shadow-2xl backdrop-blur-xl rounded-3xl p-6 sm:p-10 border border-white/10" data-aos="fade-up">
                      
                      <section className="mb-10">
                        <h2 className="text-2xl sm:text-3xl font-extrabold text-white mb-4 flex items-center gap-3 border-b border-gray-700 pb-3">
                            <Info className="text-blue-400 w-8 h-8" /> About {game.name}
                        </h2>
                        <div className="text-gray-300 leading-relaxed space-y-4 text-sm sm:text-base">
                            <p>
                              {game.desc || `Play ${game.name} for free on GameZone! This exciting game features amazing graphics and smooth gameplay. Challenge yourself and see if you can beat the high score.`}
                            </p>
                            <p>
                              Welcome to the ultimate <strong>{game.name}</strong> experience! If you are looking for a thrilling browser game that requires no downloads and can be played instantly, you are in the right place. {game.name} is designed to offer endless hours of fun for players of all ages. Whether you are playing on your mobile phone, tablet, or PC, you can enjoy seamless gameplay right here at Game Zone Hub. We pride ourselves on offering high-quality, free online games, and this title is no exception. Dive into the world of {game.name} today and discover why it is so popular among our gaming community!
                            </p>
                        </div>
                        <div className="mt-6 flex flex-wrap gap-3">
                            <span className="px-4 py-2 bg-gradient-to-r from-blue-600/20 to-blue-400/20 border border-blue-500/30 rounded-full text-sm text-blue-300 font-semibold shadow-sm flex items-center gap-2">
                                <Tag size={14}/> Free to Play
                            </span>
                            <span className="px-4 py-2 bg-gradient-to-r from-purple-600/20 to-purple-400/20 border border-purple-500/30 rounded-full text-sm text-purple-300 font-semibold shadow-sm flex items-center gap-2">
                                <Tag size={14}/> HTML5 Browser Game
                            </span>
                            <span className="px-4 py-2 bg-gradient-to-r from-cyan-600/20 to-cyan-400/20 border border-cyan-500/30 rounded-full text-sm text-cyan-300 font-semibold shadow-sm flex items-center gap-2">
                                <Tag size={14}/> Without Downloads
                            </span>
                        </div>
                      </section>

                      <section className="mb-10">
                        <h2 className="text-2xl sm:text-3xl font-extrabold text-white mb-4 flex items-center gap-3 border-b border-gray-700 pb-3">
                             <Keyboard className="text-yellow-400 w-8 h-8" /> How to Play {game.name}
                        </h2>
                        <div className="bg-black/40 rounded-2xl p-6 border border-white/5">
                          <p className="text-gray-300 mb-4 leading-relaxed font-medium">
                            {game.info || "Follow the on-screen instructions to master the game controls."}
                          </p>
                          <p className="text-gray-400 text-sm leading-relaxed">
                            Mastering the controls in <strong>{game.name}</strong> is the key to achieving the highest scores. Make sure to understand the fundamental mechanics before taking on harder challenges. Our state-of-the-art HTML5 technology ensures that the game physics and mechanics are smooth and responsive, giving you the best control possible. Practicing your timing and precision will help you conquer the game faster.
                          </p>
                        </div>
                      </section>

                       <section className="mb-10">
                        <h2 className="text-2xl sm:text-3xl font-extrabold text-white mb-4 flex items-center gap-3 border-b border-gray-700 pb-3">
                             <Trophy className="text-purple-400 w-8 h-8" /> Key Features
                        </h2>
                        <div className="grid sm:grid-cols-2 gap-4">
                            <div className="bg-gradient-to-br from-black/40 to-black/20 p-5 rounded-xl border border-white/5 flex items-start gap-4 hover:border-blue-500/30 transition-colors">
                                <div className="p-3 bg-blue-500/20 rounded-lg"><Star className="text-blue-400" size={24} /></div>
                                <div>
                                    <h4 className="font-bold text-white text-base mb-1">High Quality Graphics</h4>
                                    <p className="text-sm text-gray-400">Enjoy stunning visuals and smooth animations without the need for expensive hardware.</p>
                                </div>
                            </div>
                            <div className="bg-gradient-to-br from-black/40 to-black/20 p-5 rounded-xl border border-white/5 flex items-start gap-4 hover:border-cyan-500/30 transition-colors">
                                <div className="p-3 bg-cyan-500/20 rounded-lg"><RotateCcw className="text-cyan-400" size={24} /></div>
                                <div>
                                    <h4 className="font-bold text-white text-base mb-1">Instant Play</h4>
                                    <p className="text-sm text-gray-400">Jump right into the action with zero loading times and absolutely no downloads required.</p>
                                </div>
                            </div>
                            <div className="bg-gradient-to-br from-black/40 to-black/20 p-5 rounded-xl border border-white/5 flex items-start gap-4 hover:border-purple-500/30 transition-colors">
                                <div className="p-3 bg-purple-500/20 rounded-lg"><Joystick className="text-purple-400" size={24} /></div>
                                <div>
                                    <h4 className="font-bold text-white text-base mb-1">Engaging Gameplay</h4>
                                    <p className="text-sm text-gray-400">Experience captivating level designs and challenges that keep you coming back for more.</p>
                                </div>
                            </div>
                            <div className="bg-gradient-to-br from-black/40 to-black/20 p-5 rounded-xl border border-white/5 flex items-start gap-4 hover:border-green-500/30 transition-colors">
                                <div className="p-3 bg-green-500/20 rounded-lg"><Gamepad2 className="text-green-400" size={24} /></div>
                                <div>
                                    <h4 className="font-bold text-white text-base mb-1">100% Free</h4>
                                    <p className="text-sm text-gray-400">Enjoy this fantastic game completely free, without ever opening your wallet.</p>
                                </div>
                            </div>
                        </div>
                      </section>

                      <section className="mb-10">
                        <h2 className="text-2xl sm:text-3xl font-extrabold text-white mb-4 flex items-center gap-3 border-b border-gray-700 pb-3">
                            <span className="text-3xl">💡</span> Tips & Strategies for Success
                        </h2>
                        <p className="text-gray-300 leading-relaxed mb-4">
                          To excel at <strong>{game.name}</strong>, you need to develop a solid strategy. Start by familiarizing yourself with the early levels or basic mechanics before taking big risks. Pay attention to repeating patterns, enemy behaviors, or physics constraints, and use the tools provided wisely. 
                        </p>
                        <p className="text-gray-300 leading-relaxed">
                          Staying patient and observing how the game reacts to your inputs will dramatically improve your win rate. Don't be afraid to make mistakes—each failure is a stepping stone to mastering the game. Take breaks if a level gets too frustrating, and come back with a fresh perspective!
                        </p>
                      </section>

                      <section>
                        <h2 className="text-2xl sm:text-3xl font-extrabold text-white mb-6 flex items-center gap-3 border-b border-gray-700 pb-3">
                            <span className="text-3xl text-pink-400">❓</span> Frequently Asked Questions (FAQ)
                        </h2>
                        
                        <div className="space-y-6">
                          <div>
                            <h3 className="font-bold text-lg text-blue-300 mb-2">1. Is {game.name} free to play?</h3>
                            <p className="text-gray-400 pl-4 border-l-2 border-white/10">Yes, {game.name} is completely free to play here at Game Zone Hub. You don't need to pay anything to enjoy the full game, with no hidden fees or subscriptions required.</p>
                          </div>
                          <div>
                            <h3 className="font-bold text-lg text-blue-300 mb-2">2. Do I need to download {game.name}?</h3>
                            <p className="text-gray-400 pl-4 border-l-2 border-white/10">No, there are no downloads required. You can play {game.name} instantly in your web browser utilizing modern HTML5 technology. This keeps your device safe and saves storage space.</p>
                          </div>
                          <div>
                            <h3 className="font-bold text-lg text-blue-300 mb-2">3. Can I play {game.name} on my phone?</h3>
                            <p className="text-gray-400 pl-4 border-l-2 border-white/10">{game.mobile ? 'Absolutely! This game is fully optimized for mobile devices, meaning you can play it flawlessly on your smartphone or tablet.' : 'This specific game is fully optimized for PC and laptop browsers for the best experience. However, we have thousands of other mobile-friendly games available on our site!'}</p>
                          </div>
                          <div>
                            <h3 className="font-bold text-lg text-blue-300 mb-2">4. Is it safe to play games on Game Zone Hub?</h3>
                            <p className="text-gray-400 pl-4 border-l-2 border-white/10">Yes! We thoroughly test our games to ensure they are safe, secure, and free from viruses or malware. Your safety and privacy are our top priorities.</p>
                          </div>
                        </div>
                      </section>

                  </article>

              </div>

              <div className="lg:col-span-1">
                  <div className="bg-white/5 backdrop-blur-xl rounded-2xl p-6 border border-white/10 sticky top-24">
                      <h3 className="md:text-2xl sm:text-xl text-lg font-bold text-white mb-6 border-b border-white/10 pb-2">
                          More Like This
                      </h3>
                      <div className="space-y-4">
                          {relatedGames.map((relatedGame, idx) => (
                              <Link 
                                key={idx} 
                                to={`/play/${createSlug(relatedGame.name)}`} 
                                state={{ game: relatedGame }}
                                className="flex gap-4 items-center group p-2 hover:bg-white/5 rounded-xl transition-all"
                              >
                                  <img 
                                    src={relatedGame.image} 
                                    alt={relatedGame.name} 
                                    className="w-20 h-20 rounded-lg object-cover group-hover:scale-105 transition-transform"
                                  />
                                  <div>
                                      <h4 className="text-white font-bold text-sm group-hover:text-blue-400 transition-colors line-clamp-1">
                                          {relatedGame.name}
                                      </h4>
                                      <span className="text-xs text-gray-400 mt-1 block">Play Now</span>
                                  </div>
                              </Link>
                          ))}
                          {relatedGames.length === 0 && (
                              <p className="text-gray-400 text-sm">No related games found.</p>
                          )}
                      </div>
                      <Link to="/games" className="block text-center mt-6 text-blue-400 text-sm hover:underline">
                          View All Games →
                      </Link>
                  </div>
              </div>

          </div>


          {isFullscreen && (
            <div className="fixed bottom-4 sm:bottom-6 left-1/2 transform -translate-x-1/2 z-50 px-4" data-aos="fade-up">
              <div className="bg-black/80 backdrop-blur-lg text-white px-4 sm:px-6 py-2 sm:py-3 rounded-full border border-white/20 animate-bounce">
                <p className="text-xs sm:text-sm font-semibold text-center">
                  Press <kbd className="bg-white/20 px-1 sm:px-2 py-1 rounded text-xs">ESC</kbd> or click "Exit Fullscreen" to exit
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

    </motion.div>
  );
};

export default PlayGame;